import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserAcademyName, getUserDisplayName } from '../api/auth';
import { AcademyMember, membersAPI, normalizeMembersPayload } from '../api/members';
import { useAuth } from '../context/AuthContext';

type StudentSortKey = 'name' | 'schoolName' | 'memberClass' | 'memo';
type SortDirection = 'asc' | 'desc';

type StudentRow = {
  key: string;
  tauserId: string;
  name: string;
  schoolName: string;
  memberClass: string;
  memo: string;
  parentPhone: string;
};

const normalizeText = (value: unknown): string => {
  return typeof value === 'string' ? value.trim() : '';
};

const sanitizePhone = (phone: string): string => {
  return phone.replace(/[^0-9+]/g, '');
};

const getMemberKey = (member: AcademyMember, index: number): string => {
  const keyParts = [member.id, member.email, member.phone, member.uniqueKey].filter(Boolean);
  return keyParts.length > 0 ? keyParts.join('-') : `member-${index}`;
};

const getTauserId = (member: AcademyMember): string => {
  const rawId = member.tauser_id ?? member.tauserId ?? member.id;

  if (typeof rawId === 'number') {
    return String(rawId);
  }

  return normalizeText(rawId);
};

const extractErrorMessage = (error: any): string => {
  return String(
    error?.response?.data?.message || error?.response?.data || error?.message || '알 수 없는 오류'
  );
};

const AdminGroupMessagePage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState<AcademyMember[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(true);
  const [membersMessage, setMembersMessage] = useState('');
  const [studentSortKey, setStudentSortKey] = useState<StudentSortKey>('name');
  const [studentSortDirection, setStudentSortDirection] = useState<SortDirection>('asc');
  const [searchQuery1, setSearchQuery1] = useState('');
  const [searchQuery2, setSearchQuery2] = useState('');
  const [selectedStudentKeys, setSelectedStudentKeys] = useState<string[]>([]);

  const academyName = getUserAcademyName(user);
  const displayName = getUserDisplayName(user);

  const studentRows = useMemo<StudentRow[]>(() => {
    return members
      .map((member, index) => {
        const role = normalizeText(member.role).toLowerCase();
        if (role !== 'student') {
          return null;
        }

        return {
          key: getMemberKey(member, index),
          tauserId: getTauserId(member),
          name:
            normalizeText(member.name) ||
            normalizeText(member.username) ||
            normalizeText(member.email) ||
            `학생 ${index + 1}`,
          schoolName: normalizeText(member.schoolName) || '-',
          memberClass: normalizeText(member.class) || '-',
          memo: normalizeText(member.memo) || '-',
          parentPhone: normalizeText(member.parentPhone || member.parent_phone),
        };
      })
      .filter((row): row is StudentRow => row !== null)
      .sort((left, right) => {
        const leftValue = left[studentSortKey];
        const rightValue = right[studentSortKey];
        const compared = leftValue.localeCompare(rightValue, 'ko');

        return studentSortDirection === 'asc' ? compared : compared * -1;
      });
  }, [members, studentSortDirection, studentSortKey]);

  const filteredStudentRows = useMemo(() => {
    const q1 = searchQuery1.trim().toLowerCase();
    const q2 = searchQuery2.trim().toLowerCase();

    const matchesQuery = (row: StudentRow, query: string): boolean => {
      if (!query) {
        return true;
      }

      return (
        row.name.toLowerCase().includes(query) ||
        row.schoolName.toLowerCase().includes(query) ||
        row.memberClass.toLowerCase().includes(query) ||
        row.memo.toLowerCase().includes(query)
      );
    };

    return studentRows.filter((row) => matchesQuery(row, q1) && matchesQuery(row, q2));
  }, [searchQuery1, searchQuery2, studentRows]);

  const selectedStudents = useMemo(() => {
    if (selectedStudentKeys.length === 0) {
      return [];
    }

    const selectedSet = new Set(selectedStudentKeys);
    return studentRows.filter((row) => selectedSet.has(row.key));
  }, [selectedStudentKeys, studentRows]);

  const selectedStudentText = useMemo(() => {
    if (selectedStudents.length === 0) {
      return '-';
    }

    return selectedStudents.map((student) => student.name).join(', ');
  }, [selectedStudents]);

  const smsHref = useMemo(() => {
    const phoneNumbers = selectedStudents
      .map((student) => sanitizePhone(student.parentPhone))
      .filter((phone) => phone.length > 0);

    const uniqueNumbers = Array.from(new Set(phoneNumbers));
    if (uniqueNumbers.length === 0) {
      return undefined;
    }

    const encodedAcademyName = encodeURIComponent(academyName || '학원');
    return `sms:${uniqueNumbers.join(',')}?body=[${encodedAcademyName}]%0A`;
  }, [academyName, selectedStudents]);

  const loadMembers = useCallback(async () => {
    if (!academyName) {
      setMembers([]);
      setMembersMessage('관리자 계정에 학원명이 없어 회원 목록을 조회할 수 없습니다.');
      setIsLoadingMembers(false);
      return;
    }

    setIsLoadingMembers(true);

    try {
      setMembersMessage('');
      const response = await membersAPI.getAcademyMembers(academyName);
      setMembers(normalizeMembersPayload(response.data));
    } catch (error: any) {
      setMembersMessage(`회원 목록을 불러오지 못했습니다: ${extractErrorMessage(error)}`);
    } finally {
      setIsLoadingMembers(false);
    }
  }, [academyName]);

  useEffect(() => {
    loadMembers().catch(() => undefined);
  }, [loadMembers]);

  useEffect(() => {
    const rowKeySet = new Set(studentRows.map((row) => row.key));
    setSelectedStudentKeys((current) => current.filter((key) => rowKeySet.has(key)));
  }, [studentRows]);

  const handleStudentSort = (nextSortKey: StudentSortKey) => {
    if (studentSortKey === nextSortKey) {
      setStudentSortDirection((currentDirection) => (currentDirection === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setStudentSortKey(nextSortKey);
    setStudentSortDirection('asc');
  };

  const getStudentSortIndicator = (columnKey: StudentSortKey): string => {
    if (studentSortKey !== columnKey) {
      return 'ㆍ';
    }

    return studentSortDirection === 'asc' ? '▲' : '▼';
  };

  const toggleStudentSelection = (studentKey: string) => {
    setSelectedStudentKeys((current) => {
      if (current.includes(studentKey)) {
        return current.filter((key) => key !== studentKey);
      }

      return [...current, studentKey];
    });
  };

  const handleSelectAllVisible = () => {
    const visibleKeys = filteredStudentRows.map((row) => row.key);
    if (visibleKeys.length === 0) {
      return;
    }

    setSelectedStudentKeys((current) => {
      const merged = new Set([...current, ...visibleKeys]);
      return Array.from(merged);
    });
  };

  const handleClearAllVisible = () => {
    const visibleSet = new Set(filteredStudentRows.map((row) => row.key));
    if (visibleSet.size === 0) {
      return;
    }

    setSelectedStudentKeys((current) => current.filter((key) => !visibleSet.has(key)));
  };

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  return (
    <main className="auth-page">
      <section className="auth-card admin-card">
        <p className="auth-eyebrow">GROUP MESSAGE</p>
        <h1>{displayName} 관리자님, 반갑습니다</h1>
        <p className="auth-description">
          학생을 선택해 그룹/단체 문자 발송 대상을 준비하세요.
        </p>

        <div className="admin-action-row">
          <button
            type="button"
            className="auth-primary-button admin-refresh-button"
            onClick={() => {
              loadMembers().catch(() => undefined);
            }}
            disabled={isLoadingMembers}
          >
            {isLoadingMembers ? '갱신 중...' : '명단 갱신'}
          </button>
          <button type="button" className="auth-secondary-button admin-nav-button" onClick={() => navigate('/admin/home')}>
            관리자 홈
          </button>
          <button type="button" className="auth-secondary-button admin-logout-button" onClick={handleLogout}>
            로그아웃
          </button>
        </div>

        <div className="admin-summary-grid">
          <div className="admin-summary-item">
            <span>학원명</span>
            <strong>{academyName || '-'}</strong>
          </div>
          <div className="admin-summary-item">
            <span>학생 수</span>
            <strong>{studentRows.length}</strong>
          </div>
          <div className="admin-summary-item">
            <span>선택 인원</span>
            <strong>{selectedStudents.length}</strong>
          </div>
        </div>

        {membersMessage ? <p className="auth-inline-message">{membersMessage}</p> : null}

        {isLoadingMembers ? (
          <p className="auth-inline-message">회원 목록을 불러오는 중입니다...</p>
        ) : (
          <section className="admin-table-section admin-table-stack">
            <div className="admin-table-section-header">
              <h2>학생 테이블</h2>
              <span>
                {(searchQuery1.trim() || searchQuery2.trim())
                  ? `${filteredStudentRows.length} / ${studentRows.length}명`
                  : `${studentRows.length}명`}
              </span>
            </div>

            <div className="admin-selected-students-row">
              <p className="admin-selected-students">
                <strong>선택된 학생명단:</strong> <span>{selectedStudentText}</span>
              </p>
              <a
                className={smsHref ? 'auth-primary-button admin-send-ready-button' : 'auth-primary-button admin-send-ready-button disabled'}
                href={smsHref}
                aria-disabled={!smsHref}
                onClick={(event) => {
                  if (!smsHref) {
                    event.preventDefault();
                  }
                }}
              >
                전송 준비
              </a>
            </div>

            <div className="admin-search-row">
              <div className="admin-search-bar">
                <input
                  type="text"
                  className="admin-search-input"
                  placeholder="검색어 1 (이름·학교·반·메모)"
                  value={searchQuery1}
                  onChange={(event) => setSearchQuery1(event.target.value)}
                />
                {searchQuery1 ? (
                  <button
                    type="button"
                    className="admin-search-clear"
                    onClick={() => setSearchQuery1('')}
                    aria-label="검색어 1 지우기"
                  >
                    ✕
                  </button>
                ) : null}
              </div>

              <div className="admin-search-bar">
                <input
                  type="text"
                  className="admin-search-input"
                  placeholder="검색어 2 (이름·학교·반·메모)"
                  value={searchQuery2}
                  onChange={(event) => setSearchQuery2(event.target.value)}
                />
                {searchQuery2 ? (
                  <button
                    type="button"
                    className="admin-search-clear"
                    onClick={() => setSearchQuery2('')}
                    aria-label="검색어 2 지우기"
                  >
                    ✕
                  </button>
                ) : null}
              </div>
            </div>

            <div className="admin-selection-action-row">
              <button
                type="button"
                className="attendance-time-clear"
                onClick={handleSelectAllVisible}
                disabled={filteredStudentRows.length === 0}
              >
                현재 화면의 학생 모두 선택
              </button>
              <button
                type="button"
                className="attendance-time-clear"
                onClick={handleClearAllVisible}
                disabled={filteredStudentRows.length === 0}
              >
                현재 화면의 학생 모두 해제
              </button>
            </div>

            <div className="admin-table-shell">
              {filteredStudentRows.length > 0 ? (
                <table className="admin-member-table">
                  <thead>
                    <tr>
                      <th>
                        <button
                          type="button"
                          className="admin-sort-button"
                          onClick={() => handleStudentSort('name')}
                        >
                          학생명 <span>{getStudentSortIndicator('name')}</span>
                        </button>
                      </th>
                      <th>
                        <button
                          type="button"
                          className="admin-sort-button"
                          onClick={() => handleStudentSort('schoolName')}
                        >
                          학교 <span>{getStudentSortIndicator('schoolName')}</span>
                        </button>
                      </th>
                      <th>
                        <button
                          type="button"
                          className="admin-sort-button"
                          onClick={() => handleStudentSort('memberClass')}
                        >
                          반편성 <span>{getStudentSortIndicator('memberClass')}</span>
                        </button>
                      </th>
                      <th>
                        <button
                          type="button"
                          className="admin-sort-button"
                          onClick={() => handleStudentSort('memo')}
                        >
                          메모 <span>{getStudentSortIndicator('memo')}</span>
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudentRows.map((student) => {
                      const isSelected = selectedStudentKeys.includes(student.key);

                      return (
                        <tr
                          key={student.key}
                          className={isSelected ? 'admin-clickable-row selected-row' : 'admin-clickable-row'}
                          onClick={() => toggleStudentSelection(student.key)}
                        >
                          <td>{student.name}</td>
                          <td>{student.schoolName}</td>
                          <td>{student.memberClass}</td>
                          <td>{student.memo}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <p className="auth-inline-message">
                  {(searchQuery1.trim() || searchQuery2.trim())
                    ? '검색 결과가 없습니다.'
                    : '같은 학원명의 학생이 아직 없습니다.'}
                </p>
              )}
            </div>
          </section>
        )}
      </section>
    </main>
  );
};

export default AdminGroupMessagePage;
